import { useEffect, useRef, useState } from 'react';
import { Box, Chip, IconButton, Button, Slide, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import { useActivePromotion } from '../hooks/usePromotions';
import { useDismissedPromotion } from '../hooks/useDismissedPromotion';
import MarkdownRenderer from './MarkdownRenderer';
import PromotionModal from './PromotionModal';
import trackEvent, { AnalyticsEvent } from '../utils/trackEvent';

export default function PromotionBanner() {
  const { data: promotion } = useActivePromotion();
  const { dismissedId, dismiss } = useDismissedPromotion();
  const [modalOpen, setModalOpen] = useState(false);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const tracked = useRef(false);

  const isDismissed = promotion?.id === dismissedId;
  const visible = !!promotion && !isDismissed;

  useEffect(() => {
    if (visible && !tracked.current) {
      tracked.current = true;
      trackEvent(AnalyticsEvent.PROMOTION_BANNER_VIEW, {
        promotion_id: promotion.id,
        code: promotion.code,
      });
    }
  }, [visible, promotion]);

  if (!promotion || isDismissed) return null;

  const hasDetails = !!promotion.long_description || !!promotion.image;

  const handleCopy = () => {
    void navigator.clipboard.writeText(promotion.code);
    setCopySnackbar(true);
    trackEvent(AnalyticsEvent.PROMOTION_CODE_COPY, {
      promotion_id: promotion.id,
      code: promotion.code,
    });
  };

  const handleDismiss = () => {
    dismiss(promotion.id);
    trackEvent(AnalyticsEvent.PROMOTION_DISMISS, {
      promotion_id: promotion.id,
      code: promotion.code,
    });
  };

  const handleLearnMore = () => {
    setModalOpen(true);
    trackEvent(AnalyticsEvent.PROMOTION_LEARN_MORE, {
      promotion_id: promotion.id,
      code: promotion.code,
    });
  };

  return (
    <>
      <Slide direction="down" in mountOnEnter>
        <Box
          role="region"
          aria-label="Promotion"
          sx={{
            bgcolor: 'secondary.main',
            color: '#001A1F',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
            position: 'relative',
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
              transition: 'none',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: 'center',
              '& p': { mb: 0, color: '#001A1F' },
            }}
          >
            <MarkdownRenderer content={promotion.short_description} />
            <Chip
              label={promotion.code}
              icon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              onClick={handleCopy}
              aria-label={`Copy promotion code ${promotion.code}`}
              sx={{
                fontFamily: '"Roboto Mono", monospace',
                fontWeight: 700,
                bgcolor: 'rgba(0,0,0,0.15)',
                color: '#001A1F',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.25)' },
              }}
            />
            {hasDetails && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleLearnMore}
                sx={{
                  color: '#001A1F',
                  borderColor: 'rgba(0,0,0,0.3)',
                  '&:hover': { borderColor: '#001A1F', bgcolor: 'rgba(0,0,0,0.1)' },
                }}
              >
                Learn More
              </Button>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label="Dismiss promotion banner"
            sx={{ color: '#001A1F', position: { sm: 'absolute' }, right: { sm: 8 } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Slide>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => {
          setCopySnackbar(false);
        }}
        message="Copied!"
        aria-live="polite"
      />

      {hasDetails && (
        <PromotionModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          promotion={promotion}
        />
      )}
    </>
  );
}
